<div class="page" dir="rtl">
  <header class="hero">
    <p class="eyebrow">Catalog admin</p>
    <h1>Classify probe</h1>
    <p class="lede">
      Dry-run of the same group decision as סיווג מוצרים.
      Agreeing storefront aisles lock the group. Otherwise Gemini or ChatGPT
      classifies it, then one model call classifies the sub-group. No DB write.
    </p>
  </header>

  <form class="lookup card" (submit)="$event.preventDefault(); load()">
    <label for="classify-probe-barcode">Barcode</label>
    <div class="lookup-row">
      <input
        id="classify-probe-barcode"
        class="barcode-input"
        data-testid="classify-probe-barcode"
        name="barcode"
        type="text"
        inputmode="numeric"
        dir="ltr"
        [(ngModel)]="barcode"
        placeholder="7290002564928"
        [disabled]="busy"
      />
      <button type="submit" data-testid="classify-probe-load" [disabled]="busy">
        Load
      </button>
      <button
        type="button"
        class="secondary"
        data-testid="classify-probe-random-gold"
        (click)="loadRandomGold()"
        [disabled]="busy"
      >
        {{ pickingGold ? 'Picking…' : 'Random gold barcode' }}
      </button>
    </div>
  </form>

  <p class="error" role="alert" *ngIf="errorMessage">{{ errorMessage }}</p>
  <p class="ok" role="status" *ngIf="statusMessage">{{ statusMessage }}</p>
  <p class="muted" *ngIf="loading">Loading evidence and prompts…</p>

  <ng-container *ngIf="prepared as prep">
    <section class="card product-meta" aria-label="Product">
      <p class="product-name" dir="auto">{{ prep.displayName }}</p>
      <p class="muted" dir="auto">Raw name: {{ prep.nameRaw }}</p>
      <p class="muted" *ngIf="goldCategory && goldType" dir="ltr">
        Gold: {{ goldCategory }} / {{ goldType }}
      </p>
      <p class="muted" *ngIf="categorySlug" dir="ltr">
        Selected group: {{ categorySlug }}
      </p>
      <p class="muted">Image on file: {{ prep.hasImageAvailable ? 'yes' : 'no' }}</p>
    </section>

    <section class="card controls" aria-label="Shared settings">
      <label for="classify-probe-model">Model</label>
      <select
        id="classify-probe-model"
        data-testid="classify-probe-model"
        [(ngModel)]="selectedModel"
        [disabled]="busy || modelOptions.length === 0"
      >
        <option *ngFor="let m of modelOptions" [value]="m.id">
          {{ m.label }} ({{ m.id }})
        </option>
      </select>

      <fieldset class="photo-field">
        <legend>Include product photo?</legend>
        <label class="radio">
          <input
            type="radio"
            name="includePhoto"
            data-testid="classify-probe-photo-yes"
            [checked]="includePhoto"
            (change)="onIncludePhotoChange(true)"
            [disabled]="busy || !prep.hasImageAvailable"
          />
          Yes
        </label>
        <label class="radio">
          <input
            type="radio"
            name="includePhoto"
            data-testid="classify-probe-photo-no"
            [checked]="!includePhoto"
            (change)="onIncludePhotoChange(false)"
            [disabled]="busy"
          />
          No
        </label>
      </fieldset>
    </section>

    <!-- Plan: FastStructuredModel · Category -->
    <section
      class="card step-card"
      [class.locked]="categoryLocked"
      aria-label="FastStructuredModel Group"
    >
      <h2 dir="ltr">1. Group</h2>
      <p class="step-note" *ngIf="categoryWhy">{{ categoryWhy }}</p>

      <ng-container *ngIf="categoryDecision === 'aisle'; else categoryModelCall">
        <p class="locked-badge">Locked from storefront aisle</p>
      </ng-container>

      <ng-template #categoryModelCall>
      <ng-container *ngIf="!categoryLocked; else categoryReadonly">
        <label for="classify-probe-category-system">System prompt</label>
        <textarea
          id="classify-probe-category-system"
          class="system-prompt"
          data-testid="classify-probe-category-system"
          rows="16"
          dir="ltr"
          [(ngModel)]="categorySystem"
          [disabled]="busy"
        ></textarea>

        <label for="classify-probe-category-user">User prompt</label>
        <textarea
          id="classify-probe-category-user"
          data-testid="classify-probe-category-user"
          rows="10"
          dir="ltr"
          [(ngModel)]="categoryUser"
          [disabled]="busy"
        ></textarea>

        <button
          type="button"
          class="generate"
          data-testid="classify-probe-generate-category"
          (click)="generateCategory()"
          [disabled]="busy || !selectedModel"
        >
          {{ runningCategory ? 'Running group…' : 'Generate group' }}
        </button>
      </ng-container>

      <ng-template #categoryReadonly>
        <p class="locked-badge">Locked after Generate</p>
        <h3>System prompt</h3>
        <pre class="prompt-view" data-testid="classify-probe-category-system-ro" dir="ltr">{{ categorySystem }}</pre>
        <h3>User prompt</h3>
        <pre class="prompt-view" data-testid="classify-probe-category-user-ro" dir="ltr">{{ categoryUser }}</pre>
        <button type="button" class="generate" disabled>Generate group</button>
      </ng-template>
      </ng-template>

      <div class="result nested" *ngIf="categoryResult as run">
        <h3>Group output</h3>
        <p class="muted" dir="ltr">
          model: {{ run.model }} · image: {{ run.imageSent ? 'yes' : 'no' }} ·
          tokens {{ run.tokensIn }}/{{ run.tokensOut }} ·
          ${{ run.costUsd | number: '1.2-6' }}
        </p>
        <pre data-testid="classify-probe-category-output" dir="ltr">{{ parsedJson(run) }}</pre>
      </div>
    </section>

    <!-- Plan: FastStructuredModel · Type -->
    <section
      class="card step-card"
      *ngIf="typeReady"
      aria-label="FastStructuredModel Sub-group"
    >
      <h2 dir="ltr">
        2. FastStructuredModel · Sub-group
        <span class="muted" *ngIf="categorySlug">(under {{ categorySlug }})</span>
      </h2>
      <p class="step-note muted">
        Classification Architecture Plan — hierarchical structured-output call 2 of 2.
      </p>

      <label for="classify-probe-type-system">System prompt</label>
      <textarea
        id="classify-probe-type-system"
        class="system-prompt"
        data-testid="classify-probe-type-system"
        rows="16"
        dir="ltr"
        [(ngModel)]="typeSystem"
        [disabled]="busy"
      ></textarea>

      <label for="classify-probe-type-user">User prompt</label>
      <textarea
        id="classify-probe-type-user"
        data-testid="classify-probe-type-user"
        rows="10"
        dir="ltr"
        [(ngModel)]="typeUser"
        [disabled]="busy"
      ></textarea>

      <button
        type="button"
        class="generate"
        data-testid="classify-probe-generate-type"
        (click)="generateType()"
        [disabled]="busy || !selectedModel"
      >
        {{ runningType ? 'Running sub-group…' : 'Generate sub-group' }}
      </button>

      <div class="result nested" *ngIf="typeResult as run">
        <h3>Sub-group output</h3>
        <p class="muted" dir="ltr">
          model: {{ run.model }} · image: {{ run.imageSent ? 'yes' : 'no' }} ·
          tokens {{ run.tokensIn }}/{{ run.tokensOut }} ·
          ${{ run.costUsd | number: '1.2-6' }}
        </p>
        <pre data-testid="classify-probe-type-output" dir="ltr">{{ parsedJson(run) }}</pre>
      </div>
    </section>
  </ng-container>
</div>
